import React, { useEffect, useState, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "react-toastify";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import onsiteproLogo from '../../assets/logo2 2.png';
import './horizonTable.css';
import "../forms/formTitle.css";
import "../forms/themeCheckbox.css";
import "../forms/themePlaceholder.css";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { ensureFormUuid, noopCreateSuccess } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { canEditDeleteRecords } from "../../utils/rolePermissions";
import {
  ADMIN_THEME,
  adminEditLabelStyle,
  adminEditInputStyle,
  parseSiteRegisterDate,
  formatSiteRegisterDate,
  siteRegisterDatePickerProps,
  adminEditModalOverlayStyle,
  adminEditModalContentStyle,
  adminEditModalHeaderStyle,
  adminEditModalTitleStyle,
  adminEditModalBodyStyle,
  adminEditModalFooterStyle,
  adminEditCancelBtnStyle,
  adminEditSaveBtnStyle,
  adminEditSigBoxStyle,
} from "./adminEditModalTheme";

function LserPpmDailyChecklistPage() {
  const canEditDelete = canEditDeleteRecords();
  const [records, setRecords] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [showViewModal, setShowViewModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const generatingPdfRef = useRef(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  
  // SharePoint upload modal state
  const [showSharePointUploadModal, setShowSharePointUploadModal] = useState(false);
  const [sharePointUploadRecord, setSharePointUploadRecord] = useState(null);
  const [sharePointPhaseType, setSharePointPhaseType] = useState('phase');
  const [sharePointPhaseNumber, setSharePointPhaseNumber] = useState('15');
  const [sharePointNetwork, setSharePointNetwork] = useState('');
  const [pendingDirectUpload, setPendingDirectUpload] = useState(false);
  useEffect(() => {
    if (pendingDirectUpload && sharePointUploadRecord) {
      setPendingDirectUpload(false);
      // eslint-disable-next-line no-use-before-define
      handleSharePointUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDirectUpload, sharePointUploadRecord]);

  const [newRecord, setNewRecord] = useState({
    formUuid: "",
    userId: "",
    clientName: "",
    engineerName: "",
    site: "",
    numberOfVisits: "",
    date: "",
    numberOfCamerasList: "",
    additionalCameras: "",
    totalNumberOfCameras: "",
    commentsReport: "",
    printName: "",
    datePrintName: "",
    staffSignature: "",
    dateStaff: "",
    engineerName2: "",
    dateEngineerName2: "",
    engineerSignature: "",
    dateEngineerSignature: "",
    mcqQuestionsList: []
  });

  // Signature canvas refs
  const sigCanvasRef = useRef(null);
  const editSigCanvasRef = useRef(null);
  const engineerSigCanvasRef = useRef(null);
  const editEngineerSigCanvasRef = useRef(null);
  const itemSignatureRefs = useRef({});
  const editItemSignatureRefs = useRef({});

  // Helper function to fix unescaped quotes in JSON strings
  const fixUnescapedQuotes = (str) => {
    let result = str;
    let inString = false;
    let escaped = false;
    let fixed = '';
    
    for (let i = 0; i < result.length; i++) {
      const char = result[i];
      
      if (escaped) {
        fixed += char;
        escaped = false;
        continue;
      }
      
      if (char === '\\') {
        escaped = true;
        fixed += char;
        continue;
      }
      
      if (char === '"') {
        inString = !inString;
        fixed += char;
        continue;
      }
      
      if (inString && char === '"') {
        fixed += '\\"';
        continue;
      }
      
      fixed += char;
    }
    
    return fixed;
  };

  // Normalize MCQ data coming from API (strings, booleans, etc.)
  const normalizeMcq = (sections) => {
    if (!Array.isArray(sections)) return [];
    return sections.map(section => {
      const items = Array.isArray(section.items) ? section.items.map(item => {
        const raw = item.value;
        let value = raw;
        if (Array.isArray(item.options) && item.options.length && typeof raw === 'string') {
          const lower = raw.toLowerCase();
          if (lower === 'true') value = 'Yes';
          else if (lower === 'false') value = 'No';
        }
        const textField = item.textField || {};
        const printDate = item.printDate || {};
        const signature = item.signature || {};
        return {
          ...item,
          value,
          textField: {
            isTextBox: !!textField.isTextBox,
            hintText: textField.hintText || '',
            valueType: textField.isTextBox ? (textField.valueType || '') : '',
            value: textField.value || '',
            na: !!textField.na
          },
          printDate: {
            isPrintDate: !!printDate.isPrintDate,
            printValue: printDate.printValue || ''
          },
          signature: {
            isSigned: !!signature.isSigned,
            signValue: signature.signValue || ''
          },
          checkboxValue: !!item.checkboxValue,
          hasCheckbox: !!item.hasCheckbox
        };
      }) : [];
      return {
        ...section,
        items: items,
        checkboxOptions: section.checkboxOptions || [],
        checkboxValues: section.checkboxValues || {}
      };
    });
  };

  // Fetch all records
  const fetchRecords = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const response = await fetch(`${baseUrl}/api/lser-ppm-daily-checklist`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setRecords(data.data);
      } else {
        toast.error(data.message || "Failed to fetch records");
      }
    } catch (error) {
      console.error("Error fetching records:", error);
      toast.error("Failed to fetch records");
    }
  };

  useEffect(() => {
    async function fetchAppUsers() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/app-users", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setAppUsers(data.data);
        } else {
          setAppUsers([]);
        }
      } catch {
        setAppUsers([]);
      }
    }
    fetchRecords();
    fetchAppUsers();
  }, []);

  // Load existing signatures into edit modal canvas when editRecord changes
  useEffect(() => {
    if (editRecord && showEditModal) {
      // Load signatures after modal is fully rendered
      setTimeout(() => {
        // Load staff signature
        const staffRef = editSigCanvasRef.current;
        if (staffRef) {
          staffRef.clear();
          if (editRecord.staffSignature) {
            let sigSrc = editRecord.staffSignature;
            
            // Ensure proper data URL format
            if (typeof sigSrc === 'string') {
              if (!sigSrc.startsWith('data:image')) {
                sigSrc = 'data:image/png;base64,' + sigSrc;
              }
            }
            
            const img = new window.Image();
            img.onload = () => {
              if (staffRef) {
                const canvas = staffRef.getCanvas();
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                
                // Draw the image with proper scaling to maintain aspect ratio
                const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
                const x = (canvas.width - img.width * scale) / 2;
                const y = (canvas.height - img.height * scale) / 2;
                
                ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
              }
            };
            img.onerror = () => {
              console.error('Failed to load staff signature image, Source:', sigSrc.substring(0, 100) + '...');
            };
            img.src = sigSrc;
          } else if (staffRef) {
            staffRef.clear();
          }
        }

        // Load engineer signature
        const engineerRef = editEngineerSigCanvasRef.current;
        if (engineerRef) {
          engineerRef.clear();
          if (editRecord.engineerSignature) {
            let sigSrc = editRecord.engineerSignature;
            
            // Ensure proper data URL format
            if (typeof sigSrc === 'string') {
              if (!sigSrc.startsWith('data:image')) {
                sigSrc = 'data:image/png;base64,' + sigSrc;
              }
            }
            
            const img = new window.Image();
            img.onload = () => {
              if (engineerRef) {
                const canvas = engineerRef.getCanvas();
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                
                // Draw the image with proper scaling to maintain aspect ratio
                const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
                const x = (canvas.width - img.width * scale) / 2;
                const y = (canvas.height - img.height * scale) / 2;
                
                ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
              }
            };
            img.onerror = () => {
              console.error('Failed to load engineer signature image, Source:', sigSrc.substring(0, 100) + '...');
            };
            img.src = sigSrc;
          } else if (engineerRef) {
            engineerRef.clear();
          }
        }
      }, 500);
    }
  }, [editRecord, showEditModal]);

  // Fetch MCQ template when opening Add Modal
  const fetchMcqTemplate = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + "/api/lser-ppm-daily-checklist/template", {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success && Array.isArray(data.data)) {
        setNewRecord(prev => ({ ...prev, mcqQuestionsList: data.data }));
      }
    } catch (error) {
      console.error("Error fetching MCQ template:", error);
    }
  };

  // Signature pad handlers for Add Modal
  const clearSignature = () => {
    try {
      if (sigCanvasRef.current) {
        sigCanvasRef.current.clear();
      }
      setNewRecord(prev => ({ ...prev, staffSignature: "" }));
    } catch (error) {
      console.error('Error clearing signature:', error);
    }
  };
  const saveSignature = () => {
    if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
      // Get the full canvas instead of trimmed to maintain proper size
      const canvas = sigCanvasRef.current.getCanvas();
      const sig = canvas.toDataURL("image/png");
      setNewRecord(prev => ({
        ...prev,
        staffSignature: sig
      }));
    }
  };

  const saveEngineerSignature = () => {
    if (engineerSigCanvasRef.current && !engineerSigCanvasRef.current.isEmpty()) {
      // Get the full canvas instead of trimmed to maintain proper size
      const canvas = engineerSigCanvasRef.current.getCanvas();
      const sig = canvas.toDataURL("image/png");
      setNewRecord(prev => ({
        ...prev,
        engineerSignature: sig
      }));
    }
  };

  const clearEngineerSignature = () => {
    try {
      if (engineerSigCanvasRef.current) {
        engineerSigCanvasRef.current.clear();
      }
      setNewRecord(prev => ({ ...prev, engineerSignature: "" }));
    } catch (error) {
      console.error('Error clearing engineer signature:', error);
    }
  };

  // Signature pad handlers for Edit Modal
  const clearEditSignature = () => {
    const ref = editSigCanvasRef.current;
    if (ref) {
      ref.clear();
    }
    setEditRecord(prev => ({ ...prev, staffSignature: "" }));
  };
  const saveEditSignature = () => {
    const ref = editSigCanvasRef.current;
    if (ref && !ref.isEmpty()) {
      // Get the full canvas instead of trimmed to maintain proper size
      const canvas = ref.getCanvas();
      const sig = canvas.toDataURL("image/png");
      setEditRecord(prev => ({ ...prev, staffSignature: sig }));
    } else {
    }
  };

  const saveEditEngineerSignature = () => {
    const ref = editEngineerSigCanvasRef.current;
    if (ref && !ref.isEmpty()) {
      // Get the full canvas instead of trimmed to maintain proper size
      const canvas = ref.getCanvas();
      const sig = canvas.toDataURL("image/png");
      setEditRecord(prev => ({ ...prev, engineerSignature: sig }));
    } else {
    }
  };

  const clearEditEngineerSignature = () => {
    const ref = editEngineerSigCanvasRef.current;
    if (ref) {
      ref.clear();
    }
    setEditRecord(prev => ({ ...prev, engineerSignature: "" }));
  };

  // Item signature handlers for Add Modal - Updated to match PowAssessmentPage structure
  const clearItemSignature = (sectionIdx, itemIdx) => {
    try {
      const key = `${sectionIdx}-${itemIdx}`;
      const canvasRef = itemSignatureRefs.current[key];
      if (canvasRef && canvasRef.clear) {
        canvasRef.clear();
        // Restore the white background after clearing
        const canvas = canvasRef.getCanvas();
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      const updatedSections = [...newRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].signature.signValue = '';
      setNewRecord({ ...newRecord, mcqQuestionsList: updatedSections });
    } catch (error) {
      console.error('Error clearing item signature:', error);
    }
  };

  const saveItemSignature = (sectionIdx, itemIdx) => {
    const key = `${sectionIdx}-${itemIdx}`;
    const canvasRef = itemSignatureRefs.current[key];
    if (canvasRef && !canvasRef.isEmpty()) {
      // Get the full canvas instead of trimmed to maintain proper size
      const canvas = canvasRef.getCanvas();
      const sig = canvas.toDataURL("image/png");
      const updatedSections = [...newRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].signature.signValue = sig;
      setNewRecord({ ...newRecord, mcqQuestionsList: updatedSections });
    }
  };

  // Item signature handlers for Edit Modal - Updated to match PowAssessmentPage structure
  const clearEditItemSignature = (sectionIdx, itemIdx) => {
    try {
      if (!editRecord || !editRecord.mcqQuestionsList) {
        return;
      }
      const key = `${sectionIdx}-${itemIdx}`;
      const canvasRef = editItemSignatureRefs.current[key];
      if (canvasRef && canvasRef.clear) {
        canvasRef.clear();
        // Restore the white background after clearing
        const canvas = canvasRef.getCanvas();
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      const updatedSections = [...editRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].signature.signValue = '';
      setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
    } catch (error) {
      console.error('Error clearing edit item signature:', error);
    }
  };

  const saveEditItemSignature = (sectionIdx, itemIdx) => {
    if (!editRecord || !editRecord.mcqQuestionsList) return;
    const key = `${sectionIdx}-${itemIdx}`;
    const canvasRef = editItemSignatureRefs.current[key];
    if (canvasRef && !canvasRef.isEmpty()) {
      // Get the full canvas instead of trimmed to maintain proper size
      const canvas = canvasRef.getCanvas();
      const sig = canvas.toDataURL("image/png");
      const updatedSections = [...editRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].signature.signValue = sig;
      setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
    }
  };

  // Form handlers
  const handleTextChange = (field, value, isEdit = false) => {
    if (isEdit) {
      setEditRecord({ ...editRecord, [field]: value });
    } else {
      setNewRecord({ ...newRecord, [field]: value });
    }
  };

  const handleMcqChange = (sectionIdx, itemIdx, value, isEdit = false) => {
    if (isEdit) {
      const updatedSections = [...editRecord.mcqQuestionsList];
      const item = updatedSections[sectionIdx].items[itemIdx];
      updatedSections[sectionIdx].items[itemIdx] = {
        ...item,
        value,
        booleanValue: value === 'YES' ? true : value === 'NO' ? false : item.booleanValue
      };
      setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
    } else {
      const updatedSections = [...newRecord.mcqQuestionsList];
      const item = updatedSections[sectionIdx].items[itemIdx];
      updatedSections[sectionIdx].items[itemIdx] = {
        ...item,
        value,
        booleanValue: value === 'YES' ? true : value === 'NO' ? false : item.booleanValue
      };
      setNewRecord({ ...newRecord, mcqQuestionsList: updatedSections });
    }
  };

  const handleMcqCheckboxChange = (sectionIdx, checkboxKey, value, isEdit = false) => {
    if (isEdit) {
      const updatedSections = [...editRecord.mcqQuestionsList];
      updatedSections[sectionIdx].checkboxValues[checkboxKey] = value;
      setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
    } else {
      const updatedSections = [...newRecord.mcqQuestionsList];
      updatedSections[sectionIdx].checkboxValues[checkboxKey] = value;
      setNewRecord({ ...newRecord, mcqQuestionsList: updatedSections });
    }
  };

  const handleMcqTextFieldChange = (sectionIdx, itemIdx, value, isEdit = false) => {
    if (isEdit) {
      const updatedSections = [...editRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].textFieldValue = value;
      setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
    } else {
      const updatedSections = [...newRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].textFieldValue = value;
      setNewRecord({ ...newRecord, mcqQuestionsList: updatedSections });
    }
  };

  const handleMcqItemCheckboxChange = (sectionIdx, itemIdx, checked, isEdit = false) => {
    if (isEdit) {
      const updatedSections = [...editRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].checkboxValue = checked;
      setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
    } else {
      const updatedSections = [...newRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].checkboxValue = checked;
      setNewRecord({ ...newRecord, mcqQuestionsList: updatedSections });
    }
  };

  const handleMcqNAChange = (sectionIdx, itemIdx, checked, isEdit = false) => {
    if (isEdit) {
      const updated = [...editRecord.mcqQuestionsList];
      const it = updated[sectionIdx].items[itemIdx];
      updated[sectionIdx].items[itemIdx] = { ...it, textField: { ...it.textField, na: !!checked } };
      setEditRecord({ ...editRecord, mcqQuestionsList: updated });
    } else {
      const updated = [...newRecord.mcqQuestionsList];
      const it = updated[sectionIdx].items[itemIdx];
      updated[sectionIdx].items[itemIdx] = { ...it, textField: { ...it.textField, na: !!checked } };
      setNewRecord({ ...newRecord, mcqQuestionsList: updated });
    }
  };

  // Add Record
  const handleAdd = async () => {
    if (!newRecord.clientName || !newRecord.engineerName || !newRecord.site || !newRecord.date) {
      toast.error("All required fields must be filled");
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      
      // Save signatures before submitting
      if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
        const canvas = sigCanvasRef.current.getCanvas();
        const sig = canvas.toDataURL("image/png");
        setNewRecord(prev => ({ ...prev, staffSignature: sig }));
      }
      
      if (engineerSigCanvasRef.current && !engineerSigCanvasRef.current.isEmpty()) {
        const canvas = engineerSigCanvasRef.current.getCanvas();
        const sig = canvas.toDataURL("image/png");
        setNewRecord(prev => ({ ...prev, engineerSignature: sig }));
      }
      
      const mcqForSave = (newRecord.mcqQuestionsList || []).map(sec => ({
        ...sec,
        items: (sec.items || []).map(it => ({
          ...it,
          value: it.value || '',
          textField: {
            ...it.textField,
            valueType: it.textField?.isTextBox ? (it.textFieldValue || it.textField?.valueType || '') : ''
          }
        }))
      }));

      // Get user details for saving firstName and lastName
      const user = appUsers.find(u => u._id === newRecord.userId);
      const addedByFirstName = user ? user.firstName : '';
      const addedByLastName = user ? user.lastName : '';
      
      const payload = { 
        ...newRecord, 
        formUuid: ensureFormUuid(newRecord.formUuid),
        addedByFirstName,
        addedByLastName,
        mcqQuestionsList: JSON.stringify(mcqForSave) 
      };

      const res = await fetch(baseUrl + "/api/lser-ppm-daily-checklist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (res.ok && data.success) {
        if (noopCreateSuccess(data)) {
          toast.warn(data.message || "This record already exists. No new record was added.");
          return;
        }
        toast.success("Record added successfully");
        setShowAddModal(false);
        setNewRecord({
          formUuid: "",
          userId: "",
          clientName: "",
          engineerName: "",
          site: "",
          numberOfVisits: "",
          date: "",
          numberOfCamerasList: "",
          additionalCameras: "",
          totalNumberOfCameras: "",
          commentsReport: "",
          printName: "",
          datePrintName: "",
          staffSignature: "",
          dateStaff: "",
          engineerName2: "",
          dateEngineerName2: "",
          engineerSignature: "",
          dateEngineerSignature: "",
          mcqQuestionsList: []
        });
        fetchRecords();
      } else {
        toast.error(data.message || "Failed to add record");
      }
    } catch (error) {
      console.error("Error adding record:", error);
      toast.error("Failed to add record");
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const response = await fetch(`${baseUrl}/api/lser-ppm-daily-checklist/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Record deleted successfully");
        fetchRecords();
        setShowDeleteModal(false);
        setDeleteId(null);
      } else {
        toast.error(data.message || "Failed to delete record");
      }
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete record");
    }
  };

  // SharePoint Upload Handler
  const handleSharePointUpload = async () => {
    const siteNameForUpload = (sharePointUploadRecord?.site || '').trim();

    if (!siteNameForUpload) {
      toast.error('Site name is missing for this record.');
      return;
    }

    if (sharePointPhaseType === 'phase' && (!sharePointNetwork || sharePointNetwork.trim() === '')) {
      toast.error('Please select an area (EPN, LPN, or SPN)');
      return;
    }

    const payloadPhaseType = sharePointPhaseType === 'phase' ? 'phase' : 'non-phase';
    const payloadNetwork = sharePointPhaseType === 'phase' ? sharePointNetwork : '';
    const payloadPhaseNumber = sharePointPhaseType === 'phase' ? sharePointPhaseNumber : '';

    setShowSharePointUploadModal(false);

    await generatePDF(sharePointUploadRecord, {
      uploadOnly: true,
      phaseType: payloadPhaseType,
      phaseNumber: payloadPhaseNumber,
      network: payloadNetwork,
      siteName: siteNameForUpload,
      onSuccess: () => {
        setSharePointUploadRecord(null);
        setSharePointPhaseType('phase');
        setSharePointPhaseNumber('15');
        setSharePointNetwork('');
      },
      onError: () => {}
    });
  };

  const generatePDF = async (record, options = {}) => {
    // Prevent duplicate calls
    if (generatingPdfRef.current) {
      return;
    }
    generatingPdfRef.current = true;
    
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? {
      phaseType: options.phaseType || 'non-phase',
      phaseNumber: options.phaseNumber || '',
      network: options.network || '',
      siteName: options.siteName || '',
      onSuccess: options.onSuccess,
      onError: options.onError
    } : null;
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Modern color scheme
      const primaryColor = [139, 22, 52]; // #8b1634
      const secondaryColor = [233, 207, 214]; // #e9cfd6
      const headerColor = [139, 22, 52]; // Dark magenta
      
      // Helper function to draw modern rounded rectangle
      const drawRoundedRect = (x, y, width, height, radius, fillColor, strokeColor, hasShadow = false) => {
        if (hasShadow) {
          doc.setFillColor(0, 0, 0, 0.1);
          doc.roundedRect(x + 1, y + 1, width, height, radius, radius, 'F');
        }
        doc.setFillColor(...fillColor);
        doc.setDrawColor(...strokeColor);
        doc.setLineWidth(0.5);
        doc.roundedRect(x, y, width, height, radius, radius, 'FD');
      };
      
      // ---- HEADER SECTION ----
      const headerHeight = 30;
      drawRoundedRect(0, 0, pageWidth, headerHeight, 0, primaryColor, primaryColor);
      
      // ---- ADD HIGH-RES LOGO ----
      const addLogoToPDF = () => {
        return new Promise((resolve) => {
          const logoImg = new Image();
          logoImg.crossOrigin = 'anonymous';
          logoImg.onload = function () {
            try {
              const logoWidth = 70;
              const aspectRatio = logoImg.height / logoImg.width;
              const logoHeight = logoWidth * aspectRatio;
              const logoX = 10;
              const logoY = (headerHeight - logoHeight) / 2;
              
              const canvas = document.createElement('canvas');
              const scale = 2.5;
              canvas.width = logoImg.width * scale;
              canvas.height = logoImg.height * scale;
              const ctx = canvas.getContext('2d');
              ctx.scale(scale, scale);
              ctx.drawImage(logoImg, 0, 0);
              
              const highResDataUrl = canvas.toDataURL('image/png', 1.0);
              doc.addImage(highResDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight, '', 'NONE');
            } catch (error) {
              doc.setFillColor(255, 255, 255);
              doc.rect(10, 6, 60, 15, 'F');
              doc.setFont("helvetica", "bold");
              doc.setFontSize(10);
              doc.setTextColor(0, 0, 0);
              doc.text("TAYLOR TECHNOLOGY", 15, 12);
              doc.text("SYSTEMS LIMITED", 15, 17);
            }
            resolve();
          };
          logoImg.onerror = function () {
            doc.setFillColor(255, 255, 255);
            doc.rect(10, 6, 60, 15, 'F');
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text("TAYLOR TECHNOLOGY", 15, 12);
            doc.text("SYSTEMS LIMITED", 15, 17);
            resolve();
          };
          logoImg.src = onsiteproLogo;
        });
      };
      
      await addLogoToPDF();
      
      // ---- HEADER TEXT ----
      const titleX = 90;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text("LSER PPM Daily Checklist Report", titleX, 13);
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", titleX, 19);
      
      let yPosition = headerHeight + 10;
      
      // ---- Section 1: Basic Information ----
      doc.setFillColor(139, 22, 52);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      
      doc.rect(10, yPosition, pageWidth - 20, 8, "F");
      doc.text("1", 14, yPosition + 5.5);
      doc.text("Basic Information", 22, yPosition + 5.5);
      yPosition += 8;
      
      autoTable(doc, {
        startY: yPosition,
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 70 },
          2: { cellWidth: 30 },
          3: { cellWidth: 40 },
        },
        head: [
          [
            { content: "Client Name:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.clientName || "", styles: { halign: "center" } },
            { content: "Engineer Name:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
            { content: record.engineerName || "", styles: { halign: "center" } },
          ],
          [
            { content: "Site:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.site || "", styles: { halign: "center" } },
            { content: "Date:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
            { content: record.date || "", styles: { halign: "center" } },
          ],
          [
            { content: "Number of Visits:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.numberOfVisits || "", styles: { halign: "center" } },
            { content: "Number of Cameras:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
            { content: record.numberOfCamerasList || "", styles: { halign: "center" } },
          ],
          [
            { content: "Additional Cameras:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.additionalCameras || "", styles: { halign: "center" } },
            { content: "Total Cameras:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
            { content: record.totalNumberOfCameras || "", styles: { halign: "center" } },
          ],
        ],
        body: [
          [
            { content: "Comments Report:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.commentsReport || "", styles: { halign: "center" } },
            { content: "Print Name:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
            { content: record.printName || "", styles: { halign: "center" } },
          ],
        ],
        styles: {
          font: "helvetica",
          fontSize: 9,
          cellPadding: 3,
          overflow: "linebreak",
          valign: "middle",
        },
        theme: "grid",
        tableWidth: pageWidth - 20,
        margin: { left: 10 },
        headStyles: {
          fillColor: [245, 245, 245],
          textColor: [0, 0, 0],
          lineWidth: 0.2,
        },
        bodyStyles: {
          lineWidth: 0.2,
          textColor: [0, 0, 0],
        },
      });
      
      yPosition = doc.lastAutoTable.finalY + 2;
      
      // ---- Section 2: MCQ Sections ----
      let mcqSections = [];
      try {
        let str = record.mcqQuestionsList;
        if (typeof str === 'string') {
          try {
            mcqSections = JSON.parse(str);
            if (!Array.isArray(mcqSections)) {
              mcqSections = [];
            }
          } catch (parseError) {
            // Try to normalize and parse
            try {
              mcqSections = normalizeMcq(JSON.parse(str));
            } catch (normalizedError) {
              mcqSections = normalizeMcq(str);
            }
          }
        } else if (Array.isArray(str)) {
          mcqSections = normalizeMcq(str);
        }
      } catch (err) {
        mcqSections = [];
      }
      
      if (mcqSections && mcqSections.length > 0) {
        mcqSections.forEach((section, sectionIdx) => {
          // Check if we need a new page
          if (yPosition + 50 > pageHeight - 30) {
            doc.addPage();
            yPosition = 20;
          }
          
          // Section Header
          doc.setFillColor(139, 22, 52);
          doc.setTextColor(255, 255, 255);
          doc.setFont("helvetica", "bold");
          doc.rect(10, yPosition, pageWidth - 20, 8, "F");
          doc.text(`${sectionIdx + 2}`, 14, yPosition + 5.5);
          doc.text(section.header || `Section ${sectionIdx + 1}`, 22, yPosition + 5.5);
          yPosition += 8;
          
          // Checklist Items
          if (section.items && Array.isArray(section.items) && section.items.length > 0) {
            const tableBody = section.items.map(item => {
              const selectedValue = item.value || '';
              return [
                { content: item.title || '', styles: { halign: "left" } },
                { content: selectedValue || 'N/A', styles: { halign: "center" } },
              ];
            });
            
            autoTable(doc, {
              startY: yPosition,
              head: [
                [
                  { content: "Checklist Item", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } },
                  { content: "Value", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
                ],
              ],
              body: tableBody,
              columnStyles: {
                0: { cellWidth: 'auto' },
                1: { cellWidth: 30, halign: "center" },
              },
              styles: {
                font: "helvetica",
                fontSize: 9,
                cellPadding: 4,
                halign: "left",
                valign: "middle",
              },
              theme: "grid",
              tableWidth: pageWidth - 20,
              margin: { left: 10 },
              headStyles: {
                textColor: [0, 0, 0],
                fillColor: [245, 245, 245],
                lineWidth: 0.2,
              },
              bodyStyles: {
                lineWidth: 0.2,
              },
            });
            
            yPosition = doc.lastAutoTable.finalY + 2;
          }
        });
      }
      
      // ---- Section: Signatories ----
      if (yPosition + 60 > pageHeight - 30) {
        doc.addPage();
        yPosition = 20;
      }
      
      doc.setFillColor(139, 22, 52);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.rect(10, yPosition, pageWidth - 20, 10, "F");
      doc.text("Section : Signatories", 15, yPosition + 7);
      yPosition += 18;
      
      // Two signature boxes side by side
      const boxWidth = (pageWidth - 30) / 2;
      const boxHeight = 40;
      
      // Helper function to add signature with high quality - same as UkpnReactivePage
      const addSignatureToPDF = async (signatureData, x, y, maxWidth, maxHeight) => {
        if (!signatureData) return;
        
        try {
          // Ensure proper data URL format
          let sigSrc = signatureData;
          if (!sigSrc.startsWith('data:image')) {
            sigSrc = 'data:image/png;base64,' + sigSrc;
          }
          
          return new Promise((resolve) => {
            const sigImg = new Image();
            sigImg.crossOrigin = 'anonymous';
            
            sigImg.onload = () => {
              try {
                // Calculate dimensions maintaining aspect ratio while fitting within constraints
                const aspectRatio = sigImg.width / sigImg.height;
                
                // Calculate dimensions based on width constraint
                let imgWidth = maxWidth;
                let imgHeight = maxWidth / aspectRatio;
                
                // If height exceeds maxHeight, recalculate based on height constraint
                if (imgHeight > maxHeight) {
                  imgHeight = maxHeight;
                  imgWidth = maxHeight * aspectRatio;
                }
                
                // Ensure both dimensions don't exceed their maximums (safety check)
                imgWidth = Math.min(imgWidth, maxWidth);
                imgHeight = Math.min(imgHeight, maxHeight);
                
                // Use higher scale factor for better quality (4x for very crisp rendering)
                const scale = 4;
                
                // Create high-resolution canvas
                const canvas = document.createElement('canvas');
                canvas.width = Math.round(imgWidth * scale);
                canvas.height = Math.round(imgHeight * scale);
                
                const ctx = canvas.getContext('2d');
                
                // Set high-quality rendering options for crisp signatures
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                
                // Clear canvas with white background
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                
                // Scale the image up using high-quality rendering
                ctx.drawImage(
                  sigImg, 
                  0, 0, sigImg.width, sigImg.height,  // Source rectangle (original size)
                  0, 0, canvas.width, canvas.height   // Destination rectangle (scaled up)
                );
                
                // Convert to high-res data URL with maximum quality (1.0 = no compression loss)
                const highResDataUrl = canvas.toDataURL('image/png', 1.0);
                
                // Add to PDF with no compression ('NONE') to preserve quality
                doc.addImage(highResDataUrl, 'PNG', x, y, imgWidth, imgHeight, '', 'NONE');
              } catch (err) {
                console.error('Error adding signature to PDF:', err);
                // Fallback: try direct addition without canvas processing
                try {
                  doc.addImage(sigSrc, 'PNG', x, y, maxWidth, maxHeight);
                } catch (fallbackErr) {
                  console.error('Fallback signature addition also failed:', fallbackErr);
                }
              }
              resolve();
            };
            
            sigImg.onerror = () => {
              console.error('Error loading signature image');
              resolve();
            };
            
            sigImg.src = sigSrc;
          });
        } catch (err) {
          console.error('Signature processing error:', err);
        }
      };
      
      // Staff Signature box
      doc.setDrawColor(200, 200, 200);
      doc.setFillColor(248, 248, 248);
      doc.roundedRect(10, yPosition, boxWidth, boxHeight, 3, 3, "FD");
      
      doc.setTextColor(139, 22, 52);
      doc.setFontSize(10);
      doc.text("Staff Signature", 15, yPosition + 8);
      
      // Define exact same size constraints for both signatures
      const signatureMaxWidth = 50;
      const signatureMaxHeight = 22;
      const signatureX = 15;
      const signatureY = yPosition + 12;
      
      // Add staff signature using helper function with exact constraints
      await addSignatureToPDF(record.staffSignature, signatureX, signatureY, signatureMaxWidth, signatureMaxHeight);
      
      // Engineer Signature box
      const engineerBoxX = 10 + boxWidth + 10;
      doc.setDrawColor(200, 200, 200);
      doc.setFillColor(248, 248, 248);
      doc.roundedRect(engineerBoxX, yPosition, boxWidth, boxHeight, 3, 3, "FD");
      
      doc.setTextColor(139, 22, 52);
      doc.setFontSize(10);
      doc.text("Engineer Signature", engineerBoxX + 5, yPosition + 8);
      
      // Add engineer signature using EXACT same constraints as staff
      const engineerSignatureX = engineerBoxX + 5;
      await addSignatureToPDF(record.engineerSignature, engineerSignatureX, signatureY, signatureMaxWidth, signatureMaxHeight);
      
      yPosition += boxHeight + 10;
      
      // ---- Footer Section ----
      const footerY = pageHeight - 25;
      drawRoundedRect(0, footerY, pageWidth, 25, 0, [248, 249, 250], [200, 200, 200]);
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
        20,
        footerY + 8
      );
      doc.text("Page 1 of 1", pageWidth - 30, footerY + 15);
      
      // Decorative border removed
      
      // Build filename and either save or upload
      const safeName = String(record.clientName || 'LSERPPMChecklist').replace(/[\\/:"*?<>|]+/g, '-');
      const safeDate = String(record.date || 'Report').replace(/[\\/:"*?<>|]+/g, '-');
      
      let fileName;
      if (shouldUploadToSharePoint && uploadConfig) {
        fileName = buildSharePointFileName([
          record.clientName,
          uploadConfig.siteName,
          toDdMmYyyy(record.date),
        ]);
      } else {
        // For regular download, use full format with prefix and date
        fileName = `LSERPPMChecklist_${safeName}_${safeDate}.pdf`;
      }
      
      if (shouldUploadToSharePoint && uploadConfig) {
        try {
          const key = `${record._id || ''}|${record.site || ''}|${record.date || ''}`;
          setUploadingKey(key);
          const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
          const dataUri = doc.output('datauristring');
          
          // Prepare SharePoint upload parameters
          const uploadParams = {
            fileName,
            fileBase64: dataUri,
            phaseType: uploadConfig.phaseType,
            phaseNumber: uploadConfig.phaseNumber,
            network: uploadConfig.network,
            siteName: uploadConfig.siteName, uploadContext: "reactive-southeastern-tablets", uploadSubFolder: "LSER PPM Daily"
          };
          
          const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
            method: 'POST',
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
            body: JSON.stringify(uploadParams)
          });
          if (res.status !== 204 && !res.ok) {
            let msg = 'SharePoint upload failed';
            try {
              const out = await res.json();
              msg = out?.message || msg;
            } catch (_) {}
            throw new Error(msg);
          }
          toast.success("PDF uploaded to SharePoint successfully!");
          if (uploadConfig.onSuccess) {
            uploadConfig.onSuccess();
          }
        } catch (err) {
          toast.error(err?.message || "SharePoint upload failed");
          if (uploadConfig.onError) {
            uploadConfig.onError(err);
          }
        } finally {
          setUploadingKey(null);
        }
      } else {
        doc.save(fileName);
        toast.success("Professional LSER PPM Daily Checklist PDF generated successfully!");
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast.error('Failed to generate PDF');
    } finally {
      generatingPdfRef.current = false;
    }
  };

  // Handle view
  const handleView = (rec) => {
    let mcq = [];
    try {
      let str = rec.mcqQuestionsList;
      if (typeof str === 'string') {
        let s = str.trim();
        try {
          mcq = JSON.parse(s);
        } catch {
          // Custom parser for this specific malformed format
          try {
            
            // Parse the malformed string manually
            const parseMalformedMcq = (str) => {
              const result = [];
              let i = 0;
              
              // Skip opening bracket
              while (i < str.length && str[i] !== '[') i++;
              i++; // Skip '['
              
              while (i < str.length) {
                // Skip whitespace
                while (i < str.length && /\s/.test(str[i])) i++;
                if (i >= str.length || str[i] === ']') break;
                
                // Parse section object
                if (str[i] === '{') {
                  const section = {};
                  i++; // Skip '{'
                  
                  while (i < str.length && str[i] !== '}') {
                    // Skip whitespace
                    while (i < str.length && /\s/.test(str[i])) i++;
                    
                    // Parse property name
                    let propName = '';
                    while (i < str.length && str[i] !== ':') {
                      propName += str[i];
                      i++;
                    }
                    i++; // Skip ':'
                    
                    // Skip whitespace
                    while (i < str.length && /\s/.test(str[i])) i++;
                    
                    if (propName === 'header') {
                      // Parse header value - continue until we hit 'items:'
                      let headerValue = '';
                      while (i < str.length) {
                        // Check if we're about to hit 'items:'
                        if (str.substring(i, i + 6) === 'items:') {
                          break;
                        }
                        headerValue += str[i];
                        i++;
                      }
                      section.header = headerValue.trim().replace(/,$/, '');
                    } else if (propName === 'items') {
                      // Parse items array
                      section.items = [];
                      i++; // Skip '['
                      
                      while (i < str.length && str[i] !== ']') {
                        // Skip whitespace
                        while (i < str.length && /\s/.test(str[i])) i++;
                        if (i >= str.length || str[i] === ']') break;
                        
                        // Parse item object
                        if (str[i] === '{') {
                          const item = {};
                          i++; // Skip '{'
                          
                          while (i < str.length && str[i] !== '}') {
                            // Skip whitespace
                            while (i < str.length && /\s/.test(str[i])) i++;
                            
                            // Parse property name
                            let itemPropName = '';
                            while (i < str.length && str[i] !== ':') {
                              itemPropName += str[i];
                              i++;
                            }
                            i++; // Skip ':'
                            
                            // Skip whitespace
                            while (i < str.length && /\s/.test(str[i])) i++;
                            
                            if (itemPropName === 'title') {
                              // Parse title value - continue until we hit 'options:' or 'value:'
                              let titleValue = '';
                              while (i < str.length) {
                                // Check if we're about to hit 'options:' or 'value:'
                                if (str.substring(i, i + 8) === 'options:' || str.substring(i, i + 6) === 'value:') {
                                  break;
                                }
                                titleValue += str[i];
                                i++;
                              }
                              item.title = titleValue.trim().replace(/,$/, '');
                            } else if (itemPropName === 'options') {
                              // Parse options array
                              item.options = [];
                              i++; // Skip '['
                              
                              let optionValue = '';
                              while (i < str.length && str[i] !== ']') {
                                if (str[i] === ',') {
                                  item.options.push(optionValue.trim());
                                  optionValue = '';
                                } else {
                                  optionValue += str[i];
                                }
                                i++;
                              }
                              if (optionValue.trim()) {
                                item.options.push(optionValue.trim());
                              }
                            } else if (itemPropName === 'value') {
                              // Parse value
                              let valueValue = '';
                              while (i < str.length && str[i] !== ',' && str[i] !== '}') {
                                valueValue += str[i];
                                i++;
                              }
                              item.value = valueValue.trim();
                            }
                            
                            // Skip comma
                            if (i < str.length && str[i] === ',') i++;
                          }
                          
                          section.items.push(item);
                        }
                        
                        // Skip comma
                        if (i < str.length && str[i] === ',') i++;
                      }
                    }
                    
                    // Skip comma
                    if (i < str.length && str[i] === ',') i++;
                  }
                  
                  result.push(section);
                }
                
                // Skip comma
                if (i < str.length && str[i] === ',') i++;
              }
              
              return result;
            };
            
            // Use a simpler regex-based approach
            mcq = [];
             // Use a completely different approach - parse sections by finding boundaries
             const sections = [];
             
             // Split by },{header: to find section boundaries
             const sectionParts = str.split(/},\{header:/);
             
             for (let i = 0; i < sectionParts.length; i++) {
               let sectionStr = sectionParts[i];
               
               // Clean up the section string
               if (i === 0) {
                 sectionStr = sectionStr.replace(/^\[/, '').replace(/^\{header:/, '');
               } else {
                 sectionStr = 'header:' + sectionStr;
               }
               
               if (i === sectionParts.length - 1) {
                 sectionStr = sectionStr.replace(/}\]$/, '');
               } else {
                 sectionStr = sectionStr + '}';
               }
               
               // Parse header and items from this section
               const headerMatch = sectionStr.match(/header:([^,]+(?:,[^,]+)*?),items:\[(.*)\]$/);
               if (headerMatch) {
                 const section = {
                   header: headerMatch[1].trim(),
                   items: []
                 };
                 
                 const itemsStr = headerMatch[2];
                 
                 // Split items by },{title:
                 const itemParts = itemsStr.split(/},{title:/);
                 
                 for (let j = 0; j < itemParts.length; j++) {
                   let itemStr = itemParts[j];
                   
                   // Clean up the item string
                   if (j === 0) {
                     itemStr = itemStr.replace(/^\{title:/, '');
                   } else {
                     itemStr = 'title:' + itemStr;
                   }
                   
                   if (j === itemParts.length - 1) {
                     itemStr = itemStr.replace(/}$/, '');
                   } else {
                     itemStr = itemStr + '}';
                   }
                   
                   // Parse individual item
                   const titleMatch = itemStr.match(/title:([^,]+(?:,[^,]+)*?),options:\[([^\]]+)\],value:([^,}]+)/);
                   if (titleMatch) {
                     const item = {
                       title: titleMatch[1].trim(),
                       options: titleMatch[2].split(',').map(opt => opt.trim()),
                       value: titleMatch[3].trim()
                     };
                     section.items.push(item);
                   }
                 }
                 
                 sections.push(section);
               }
             }
             
             mcq = sections;
          } catch (innerError) {
            console.error('Failed to parse with custom parser:', innerError);
            mcq = [];
          }
        }
      } else {
        mcq = str;
      }
    } catch (e) {
      console.error('Error parsing MCQ:', e);
    }
    setViewRecord({ ...rec, mcqQuestionsList: normalizeMcq(mcq) });
    setShowViewModal(true);
  };

  // Handle edit
  const handleEdit = (rec) => {
    let mcq = [];
    try {
      
      let str = rec.mcqQuestionsList;
      if (typeof str === 'string') {
        let s = str.trim();
        try {
          mcq = JSON.parse(s);
        } catch {
          // Custom parser for this specific malformed format
          try {
            
            // Use a completely different approach - parse sections by finding boundaries
            const sections = [];
            
            
            // Split by },{header: to find section boundaries
            const sectionParts = str.split(/},\{header:/);
            
            for (let i = 0; i < sectionParts.length; i++) {
              let sectionStr = sectionParts[i];
              
              // Clean up the section string
              if (i === 0) {
                sectionStr = sectionStr.replace(/^\[/, '').replace(/^\{header:/, '');
              } else {
                sectionStr = 'header:' + sectionStr;
              }
              
              if (i === sectionParts.length - 1) {
                sectionStr = sectionStr.replace(/}\]$/, '');
              } else {
                sectionStr = sectionStr + '}';
              }
              
              // Parse header and items from this section
              
              // For the first section, we need to handle it differently since it doesn't have "header:" prefix
              let headerMatch;
              if (i === 0) {
                // First section: look for pattern without "header:" prefix
                headerMatch = sectionStr.match(/^([^,]+(?:,[^,]+)*?),items:\[(.*)\]$/);
              } else {
                // Other sections: look for pattern with "header:" prefix
                headerMatch = sectionStr.match(/header:([^,]+(?:,[^,]+)*?),items:\[(.*)\]$/);
              }
              
              // If the above regexes fail, try a more flexible approach
              if (!headerMatch) {
                if (i === 0) {
                  // First section: more flexible pattern - capture everything after items:[ until the end
                  headerMatch = sectionStr.match(/^([^,]+(?:,[^,]+)*?),items:\[(.*)$/);
                } else {
                  // Other sections: more flexible pattern - capture everything after items:[ until the end
                  headerMatch = sectionStr.match(/header:([^,]+(?:,[^,]+)*?),items:\[(.*)$/);
                }
              }
              
              if (headerMatch) {
                const headerText = i === 0 ? headerMatch[1] : headerMatch[1];
                const section = {
                  header: headerText.trim(),
                  items: []
                };
                
                const itemsStr = headerMatch[2];
                
                // Split items by },{title:
                const itemParts = itemsStr.split(/},{title:/);
                
                for (let j = 0; j < itemParts.length; j++) {
                  let itemStr = itemParts[j];
                  
                  // Clean up the item string
                  if (j === 0) {
                    itemStr = itemStr.replace(/^\{title:/, '');
                  } else {
                    itemStr = 'title:' + itemStr;
                  }
                  
                  if (j === itemParts.length - 1) {
                    itemStr = itemStr.replace(/}$/, '');
                  } else {
                    itemStr = itemStr + '}';
                  }
                  
                  // Parse individual item
                  const titleMatch = itemStr.match(/title:([^,]+(?:,[^,]+)*?),options:\[([^\]]+)\],value:([^,}]+)/);
                  if (titleMatch) {
                    const item = {
                      title: titleMatch[1].trim(),
                      options: titleMatch[2].split(',').map(opt => opt.trim()),
                      value: titleMatch[3].trim()
                    };
                    section.items.push(item);
                  }
                }
                
                sections.push(section);
              }
            }
            
            mcq = sections;
          } catch (innerError) {
            console.error('Failed to parse with custom parser:', innerError);
            mcq = [];
          }
        }
      } else {
        mcq = str;
      }
      
    } catch (e) {
      console.error('Error parsing MCQ:', e);
    }
    const normalized = normalizeMcq(mcq);
    setEditRecord({ ...rec, formUuid: ensureFormUuid(rec?.formUuid), mcqQuestionsList: normalized });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editRecord || !editRecord.clientName || !editRecord.engineerName || !editRecord.site || !editRecord.date) {
      toast.error("All required fields must be filled");
      return;
    }
    
    // Force save all signatures before saving the form
    const staffRef = editSigCanvasRef.current;
    if (staffRef && !staffRef.isEmpty()) {
      const canvas = staffRef.getCanvas();
      const sig = canvas.toDataURL("image/png");
      setEditRecord(prev => ({ ...prev, staffSignature: sig }));
    } else {
    }
    
    const engineerRef = editEngineerSigCanvasRef.current;
    if (engineerRef && !engineerRef.isEmpty()) {
      const canvas = engineerRef.getCanvas();
      const sig = canvas.toDataURL("image/png");
      setEditRecord(prev => ({ ...prev, engineerSignature: sig }));
    } else {
    }
    
    // Wait a bit for state updates, then save
    setTimeout(async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        
        // Get the latest signatures from refs one more time to ensure we have them
        let finalStaffSig = editRecord.staffSignature;
        let finalEngineerSig = editRecord.engineerSignature;
        
        if (staffRef && !staffRef.isEmpty()) {
          finalStaffSig = staffRef.getCanvas().toDataURL("image/png");
        }
        if (engineerRef && !engineerRef.isEmpty()) {
          finalEngineerSig = engineerRef.getCanvas().toDataURL("image/png");
        }
        
        const mcqForSave = (editRecord.mcqQuestionsList || []).map(sec => ({
          ...sec,
          items: (sec.items || []).map(it => ({
            ...it,
            value: it.value || '',
            textField: {
              ...it.textField,
              valueType: it.textField?.isTextBox ? (it.textFieldValue || it.textField?.valueType || '') : ''
            }
          }))
        }));

        // Get user details for saving firstName and lastName
        const user = appUsers.find(u => u._id === editRecord.userId);
        const addedByFirstName = user ? user.firstName : '';
        const addedByLastName = user ? user.lastName : '';
        
        const updatedRecord = { 
          ...editRecord, 
          formUuid: ensureFormUuid(editRecord.formUuid),
          addedByFirstName,
          addedByLastName,
          staffSignature: finalStaffSig, 
          engineerSignature: finalEngineerSig, 
          mcqQuestionsList: JSON.stringify(mcqForSave) 
        };

        const res = await fetch(baseUrl + "/api/lser-ppm-daily-checklist/" + editRecord._id, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(updatedRecord)
        });

        const data = await res.json();
        if (res.ok && data.success) {
          toast.success("Record updated successfully");
          setShowEditModal(false);
          setEditRecord(null);
          fetchRecords();
        } else {
          toast.error(data.message || "Failed to update record");
        }
      } catch (error) {
        console.error("Error updating record:", error);
        toast.error("Failed to update record");
      }
    }, 100);
  };

  // Search logic
  const filtered = records.filter(r => {
    const fields = [r.clientName, r.engineerName, r.site, r.date].map(val => (val || '').toLowerCase());
    const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (searchWords.length === 0) return true;
    return searchWords.every(word => fields.some(field => field.includes(word)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page-1)*pageSize, page*pageSize);
  const formatDateForDisplay = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    return isNaN(x.getTime()) ? d : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };
  
  // Reset page to 1 if current page is beyond available pages
  if (filtered.length === 0 && page > 1) {
    setPage(1);
  }

  return (
    <>
      <div style={{
        background: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        {/* Header Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1a202c',
            margin: 0
          }}>LSER PPM Daily Checklist</h2>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <i className="fa fa-search" style={{
                position: 'absolute',
                left: '12px',
                color: '#8b1634',
                fontSize: '14px'
              }}></i>
              <input 
                type="text" 
                placeholder="Type to Search" 
                value={search} 
                onChange={e=>{ setSearch(e.target.value); setPage(1); }}
                style={{
                  padding: '10px 12px 10px 36px',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  outline: 'none',
                  width: '250px',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#8b1634'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div style={{
          overflowX: 'auto',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1.2fr 1.2fr 0.9fr 1fr 1fr 1fr 1fr 1.2fr',
            background: '#f7fafc',
            padding: '12px 16px',
            borderBottom: '1px solid #e2e8f0',
            gap: '16px',
            alignItems: 'center'
          }}>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Client Name</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Engineer Name</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Site</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>No. of Visits</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Total Cameras</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Added By</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Updated Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Actions</div>
          </div>

          {/* Table Body */}
          <div>
            {paged.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#718096',
                fontSize: '16px'
              }}>No records found.</div>
            ) : paged.map((r, idx) => {
                // First check if firstName and lastName are saved in the record
                // If not, try to find user from appUsers array
                let userName = '-';
                if (r.addedByFirstName && r.addedByLastName) {
                  userName = `${r.addedByFirstName} ${r.addedByLastName}`;
                } else {
                const user = appUsers.find(u => u._id === r.userId);
                  userName = user ? `${user.firstName} ${user.lastName}` : (r.userId || '-');
                }
                return (
                  <div 
                    key={r._id || idx}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.2fr 1.2fr 1.2fr 0.9fr 1fr 1fr 1fr 1fr 1.2fr',
                      padding: '16px',
                      borderBottom: '1px solid #f1f5f9',
                      gap: '16px',
                      alignItems: 'center',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.clientName || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.engineerName || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.site || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.date || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.numberOfVisits || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.totalNumberOfCameras || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{userName || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{formatDateForDisplay(r.updatedAt || r.createdAt || r.date)}</div>
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                      {canEditDelete && (
                        <button 
                          onClick={()=>handleEdit(r)}
                          title="Edit"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <i className="fa fa-edit" style={{color:'#22c55e', fontSize: '16px'}}></i>
                        </button>
                      )}
                      <button 
                        onClick={()=>generatePDF(r)}
                        title="Generate PDF"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '6px',
                          borderRadius: '6px',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <i className="fa fa-file-pdf-o" style={{color:'#dc2626', fontSize: '16px'}}></i>
                      </button>
                      {(() => { 
                        const k = `${r._id || ''}|${r.site || ''}|${r.date || ''}`; 
                        const busy = uploadingKey === k; 
                        return (
                          <button 
                            onClick={()=>{
                              if (!busy) {
                                setSharePointUploadRecord(r);
                                setSharePointPhaseType('phase');
                                setSharePointPhaseNumber('15');
                                setSharePointNetwork('');
                                setSharePointPhaseType("non-phase"); setPendingDirectUpload(true);
                              }
                            }}
                            disabled={busy}
                            title={busy ? "Uploading..." : "Upload to SharePoint"}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: busy ? 'not-allowed' : 'pointer',
                              padding: '6px',
                              borderRadius: '6px',
                              transition: 'background-color 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: busy ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => !busy && (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{color:'#22c55e', fontSize: '16px'}}></i>
                          </button>
                        ); 
                      })()}
                      {canEditDelete && (
                        <button 
                          onClick={() => {
                            setDeleteId(r._id);
                            setShowDeleteModal(true);
                          }}
                          title="Delete"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <i className="fa fa-trash" style={{color:'#e53e3e', fontSize: '16px'}}></i>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Pagination */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: '18px',
          gap: '12px'
        }}>
          <button 
            disabled={page===1 || filtered.length === 0} 
            onClick={()=>setPage(page-1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===1 || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===1 || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===1 || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Prev</button>
          <span style={{
            fontWeight: '700',
            color: '#8b1634',
            fontSize: '16px'
          }}>{page} / {totalPages}</span>
          <button 
            disabled={page===totalPages || filtered.length === 0} 
            onClick={()=>setPage(page+1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===totalPages || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===totalPages || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===totalPages || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Next</button>
        </div>
      </div>

      {/* SharePoint Upload Modal */}
      {showSharePointUploadModal && sharePointUploadRecord && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'600px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Upload to SharePoint</h5>
                <button type="button" className="btn-close" onClick={()=>{
                  setShowSharePointUploadModal(false);
                  setSharePointUploadRecord(null);
                  setSharePointPhaseType('phase');
                  setSharePointPhaseNumber('15');
                  setSharePointNetwork('');
                }}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-12">
                    <label className="form-label fw-semibold">Type <span style={{color:'red'}}>*</span></label>
                    <select 
                      className="form-select" 
                      value={sharePointPhaseType}
                      onChange={(e) => {
                        setSharePointPhaseType(e.target.value);
                        if (e.target.value === 'non-phase') {
                          setSharePointNetwork('');
                        }
                      }}
                    >
                      <option value="phase">Phase</option>
                      <option value="non-phase">Non Phase</option>
                    </select>
                  </div>
                  
                  {sharePointPhaseType === 'phase' && (
                    <>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Phase Number <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointPhaseNumber}
                          onChange={(e) => {
                            setSharePointPhaseNumber(e.target.value);
                          }}
                        >
                          <option value="15">Phase 15 (2025)</option>
                          <option value="16">Phase 16 (2026)</option>
                        </select>
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Area <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointNetwork}
                          onChange={(e) => {
                            setSharePointNetwork(e.target.value);
                          }}
                        >
                          <option value="">Select Area</option>
                          <option value="EPN">EPN</option>
                          <option value="LPN">LPN</option>
                          <option value="SPN">SPN</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={()=>{
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType('phase');
                    setSharePointPhaseNumber('15');
                    setSharePointNetwork('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn" 
                  style={{background:'#8b1634', color:'#fff', fontWeight:600}}
                  onClick={handleSharePointUpload}
                >
                  Upload to SharePoint
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload LSER PPM Daily Checklist File"
        importPath="lser-ppm-daily-checklist/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setRecords((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteId(null);
                }}></button>
              </div>
              <div className="modal-body">
            <p>Are you sure you want to delete this record? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
              <button
                  type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteId(null);
                }}
              >
                Cancel
              </button>
              <button
                  type="button"
                  className="btn btn-danger"
                onClick={() => handleDelete(deleteId)}
              >
                Delete
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Add LSER PPM Daily Checklist</h5>
                <button type="button" className="btn-close" onClick={()=>setShowAddModal(false)}></button>
                </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-2 col-4">
                    <label>User ID</label>
                    <input type="text" className="form-control" value={newRecord.userId} onChange={e=>handleTextChange("userId", e.target.value)} />
                </div>
                  <div className="mb-2 col-4">
                    <label>Client Name *</label>
                    <input type="text" className="form-control" value={newRecord.clientName} onChange={e=>handleTextChange("clientName", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Engineer Name *</label>
                    <input type="text" className="form-control" value={newRecord.engineerName} onChange={e=>handleTextChange("engineerName", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Site *</label>
                    <input type="text" className="form-control" value={newRecord.site} onChange={e=>handleTextChange("site", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Date *</label>
                    <input type="date" className="form-control" value={newRecord.date ? newRecord.date.split('/').reverse().join('-') : ''} onChange={e=>handleTextChange("date", e.target.value.split('-').reverse().join('/'))} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Number of Visits</label>
                    <input type="text" className="form-control" value={newRecord.numberOfVisits} onChange={e=>handleTextChange("numberOfVisits", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Number of Cameras List</label>
                    <input type="text" className="form-control" value={newRecord.numberOfCamerasList} onChange={e=>handleTextChange("numberOfCamerasList", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Additional Cameras</label>
                    <input type="text" className="form-control" value={newRecord.additionalCameras} onChange={e=>handleTextChange("additionalCameras", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Total Number of Cameras</label>
                    <input type="text" className="form-control" value={newRecord.totalNumberOfCameras} onChange={e=>handleTextChange("totalNumberOfCameras", e.target.value)} />
                  </div>
                  <div className="mb-2 col-6">
                    <label>Comments Report</label>
                    <textarea className="form-control" rows="3" value={newRecord.commentsReport} onChange={e=>handleTextChange("commentsReport", e.target.value)} />
                  </div>
                  <div className="mb-2 col-3">
                    <label>Print Name</label>
                    <input type="text" className="form-control" value={newRecord.printName} onChange={e=>handleTextChange("printName", e.target.value)} />
                  </div>
                  <div className="mb-2 col-3">
                    <label>Date Print Name</label>
                    <input type="date" className="form-control" value={newRecord.datePrintName ? newRecord.datePrintName.split('/').reverse().join('-') : ''} onChange={e=>handleTextChange("datePrintName", e.target.value.split('-').reverse().join('/'))} />
                  </div>
                  <div className="mb-2 col-3">
                    <label>Date Staff</label>
                    <input type="date" className="form-control" value={newRecord.dateStaff ? newRecord.dateStaff.split('/').reverse().join('-') : ''} onChange={e=>handleTextChange("dateStaff", e.target.value.split('-').reverse().join('/'))} />
                  </div>
                  <div className="mb-2 col-3">
                    <label>Engineer Name 2</label>
                    <input type="text" className="form-control" value={newRecord.engineerName2} onChange={e=>handleTextChange("engineerName2", e.target.value)} />
                  </div>
                  <div className="mb-2 col-3">
                    <label>Date Engineer Name 2</label>
                    <input type="date" className="form-control" value={newRecord.dateEngineerName2 ? newRecord.dateEngineerName2.split('/').reverse().join('-') : ''} onChange={e=>handleTextChange("dateEngineerName2", e.target.value.split('-').reverse().join('/'))} />
                  </div>
                  <div className="mb-2 col-3">
                    <label>Date Engineer Signature</label>
                    <input type="date" className="form-control" value={newRecord.dateEngineerSignature ? newRecord.dateEngineerSignature.split('/').reverse().join('-') : ''} onChange={e=>handleTextChange("dateEngineerSignature", e.target.value.split('-').reverse().join('/'))} />
              </div>

                  {/* MCQ Sections - Updated to match PowAssessmentPage structure */}
                  <div className="mb-2 col-md-6">
                    <label className="fw-bold" style={{color:'#8b1634'}}>MCQ Questions List</label>
                    {Array.isArray(newRecord.mcqQuestionsList) && newRecord.mcqQuestionsList.map((section, sidx) => (
                      <div key={sidx} style={{border:'1px solid #eee', borderRadius:8, padding:12, marginTop:8}}>
                        {section.header === 'Steps List' ? (
                          <div>
                            {/* Show main title from API data */}
                            <div className="fw-bold" style={{marginBottom:8, color:'#8b1634', fontSize:'18px'}}>
                              {section.mainTitle || 'Steps List'}
                </div>
                </div>
                        ) : (
                        <div className="fw-bold" style={{marginBottom:8}}>{section.header}</div>
                        )}
                        {Array.isArray(section.checkboxOptions) && section.checkboxOptions.length > 0 && (
                          <div className="mb-2" style={{display:'flex', flexWrap:'wrap', gap:16}}>
                            {section.checkboxOptions.map(opt => (
                              <label key={opt} style={{display:'flex', alignItems:'center', gap:6}}>
                                <input type="checkbox" checked={!!section.checkboxValues?.[opt]} onChange={e=>handleMcqCheckboxChange(sidx, opt, e.target.checked)} style={{accentColor:'#8b1634'}} /> {opt}
                                </label>
                              ))}
                  </div>
                        )}
                        {Array.isArray(section.items) && section.items.map((item, iidx) => (
                          <div key={iidx} className="mb-2" style={{paddingLeft:8}}>
                                {item.isMainTitle ? (
                                  <div className="fw-bold" style={{color:'#8b1634', fontSize:'18px', marginBottom:12, marginTop:8, }}>
                                    {item.title}
                                  </div>
                                ) : item.isStepHeader ? (
                                  <div className="fw-bold" style={{color:'#8b1634', fontSize:'16px', marginBottom:8, marginTop:12, paddingLeft:16}}>
                                    {item.title}
                                  </div>
                                ) : (
                                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6}}>
                                  <div style={{display:'flex', alignItems:'center', gap:12}}>
                                      {/* Show title and subtitle */}
                                      <div>
                                        <div className="fw-bold" style={{color:'#8b1634', fontSize:'1.1rem'}}>
                                          {item.title ? item.title.split(' - ')[0] : item.text}
                                        </div>
                                        {item.title && item.title.includes(' - ') && (
                                          <div className="fw-medium" style={{color:'#666', fontSize:'0.9rem'}}>
                                            {item.title.split(' - ')[1] || ''}
                                          </div>
                                        )}
              </div>

                                    {/* Show text input box when isTextBox: true */}
                              {item.textField?.isTextBox && (
                  <input
                    type="text"
                                    className="form-control" 
                                    style={{
                                      maxWidth: 320,
                                          fontSize: '16px',
                                      fontFamily: 'system-ui, -apple-system, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
                                      lineHeight: '1.5',
                                          padding: '8px 12px'
                                    }}
                                    placeholder={item.textField?.hintText || ''} 
                                        value={item.textFieldValue !== undefined ? item.textFieldValue : (item.textField?.valueType || '')} 
                                        onChange={e=>handleMcqTextFieldChange(sidx, iidx, e.target.value)} 
                                      />
                                    )}
                </div>
                                  
                                  {/* Show checkboxes for items with hasCheckbox: true */}
                                  {item.hasCheckbox && (
                  <input
                                      type="checkbox" 
                                      checked={!!item.checkboxValue} 
                                      onChange={e=>handleMcqItemCheckboxChange(sidx, iidx, e.target.checked)}
                                      style={{accentColor:'#8b1634'}} 
                                    />
                                  )}
                </div>
                                )}
                                
                                {/* Show radio buttons for items without hasCheckbox */}
                                {!item.hasCheckbox && !item.textField?.isTextBox && Array.isArray(item.options) && (
                                  <div style={{display:'flex', flexWrap:'wrap', gap:12, alignItems:'center', paddingLeft: item.hasCheckbox ? 0 : 20}}>
                                    {item.options.map(opt => (
                                      <label key={opt} style={{display:'flex', alignItems:'center', gap:6}}>
                                        <input type="radio" name={`add-${sidx}-${iidx}`} checked={item.value === opt} onChange={()=>handleMcqChange(sidx, iidx, opt)} style={{accentColor:'#8b1634'}} /> {opt}
                                      </label>
                                    ))}
              </div>
                                )}

                                {/* Show print date field */}
                              {item.printDate?.isPrintDate && (
                                <div style={{display:'flex', alignItems:'center', gap:12, marginTop:8}}>
                                    <label style={{fontSize:'14px', fontWeight:'500'}}>Date:</label>
                <input
                                    type="date" 
                                    className="form-control" 
                                    style={{
                                      maxWidth: 200,
                                        fontSize: '16px',
                                      fontFamily: 'system-ui, -apple-system, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
                                      lineHeight: '1.5',
                                        padding: '8px 12px'
                                    }}
                                    value={item.printDate?.printValue ? item.printDate.printValue.split('/').reverse().join('-') : ''} 
                                    onChange={e=>{
                                      const updatedSections = [...newRecord.mcqQuestionsList];
                                        updatedSections[sidx].items[iidx].printDate.printValue = e.target.value.split('-').reverse().join('/');
                                      setNewRecord({ ...newRecord, mcqQuestionsList: updatedSections });
                                    }} 
                />
              </div>
                              )}

                                {/* Show signature field */}
                              {item.signature?.isSigned && (
                                <div style={{display:'flex', flexDirection:'column', gap:8, marginTop:8}}>
                                    <label style={{fontSize:'14px', fontWeight:'500'}}>Signature:</label>
                                  <div style={{width:'300px', border:'2px solid #8b1634', borderRadius:'8px', background:'#e9cfd6', padding:'8px', minHeight:'80px'}}>
                                    <SignatureCanvas
                                      ref={el => {
                                        const key = `${sidx}-${iidx}`;
                                        itemSignatureRefs.current[key] = el;
                                      }}
                                      penColor="#8b1634"
                                      backgroundColor="#e9cfd6"
                                      clearOnResize={false}
                                      velocityFilterWeight={0.7}
                                      minWidth={1}
                                      maxWidth={2}
                                      dotSize={1}
                                      throttle={16}
                                      canvasProps={{ 
                                        width: 280, 
                                        height: 60, 
                                        style: { 
                                          width: '100%', 
                                          height: '60px', 
                                          border: 'none', 
                                          borderRadius: '8px', 
                                          background: '#e9cfd6', 
                                          display: 'block', 
                                          touchAction: 'none' 
                                        } 
                                      }}
                                      onEnd={() => saveItemSignature(sidx, iidx)}
                                    />
                                    <div style={{width:'100%', display:'flex', justifyContent:'flex-end', marginTop:'4px'}}>
                                      <button 
                                        type="button" 
                                        className="btn btn-outline-danger btn-sm rounded-pill px-2" 
                                        style={{background:'transparent', color:'#8b1634', borderColor:'#8b1634', fontSize:'12px'}} 
                                        onClick={() => clearItemSignature(sidx, iidx)}
                                      >
                                        Clear
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    ))}
              </div>

                  {/* Staff Signature */}
                  <div className="mb-2 col-md-6">
                    <label className="form-label fw-semibold text-secondary">Staff Signature</label>
                    <div style={{
                      width: '100%',
                      border: '2px solid #8b1634',
                      borderRadius: '18px',
                      background: '#e9cfd6',
                      padding: '0.5rem',
                      textAlign: 'left',
                      position: 'relative',
                      minHeight: '120px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <div style={{
                        width: '100%',
                        height: '100px',
                        position: 'relative',
                        display: 'block'
                      }}>
                      <SignatureCanvas
                        ref={sigCanvasRef}
                        penColor="#8b1634"
                          backgroundColor="white"
                        clearOnResize={false}
                        velocityFilterWeight={0.7}
                        minWidth={1}
                        maxWidth={2.5}
                        dotSize={1.5}
                        throttle={16}
                          canvasProps={{
                            width: 500,
                            height: 80,
                            style: {
                              width: '100%',
                              height: '80px',
                              border: 'none',
                              borderRadius: '12px',
                              background: 'white',
                              display: 'block',
                              touchAction: 'none',
                              cursor: 'crosshair',
                              position: 'relative'
                            }
                          }}
                        onEnd={saveSignature}
                  />
                </div>
                      <div style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '8px'
                      }}>
                        <span style={{
                          fontSize: '12px',
                          color: '#8b1634',
                          fontStyle: 'italic'
                        }}>
                          Draw your signature above
                        </span>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm rounded-pill px-3"
                          style={{
                            background: 'transparent',
                            color: '#8b1634',
                            borderColor: '#8b1634'
                          }}
                          onClick={clearSignature}>
                          Clear
                        </button>
                      </div>
                </div>
              </div>

                  {/* Engineer Signature */}
                  <div className="mb-2 col-md-6">
                    <label className="form-label fw-semibold text-secondary">Engineer Signature</label>
                    <div style={{
                      width: '100%',
                      border: '2px solid #8b1634',
                      borderRadius: '18px',
                      background: '#e9cfd6',
                      padding: '0.5rem',
                      textAlign: 'left',
                      position: 'relative',
                      minHeight: '120px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <div style={{
                        width: '100%',
                        height: '100px',
                        position: 'relative',
                        display: 'block'
                      }}>
                      <SignatureCanvas
                        ref={engineerSigCanvasRef}
                        penColor="#8b1634"
                          backgroundColor="white"
                        clearOnResize={false}
                        velocityFilterWeight={0.7}
                        minWidth={1}
                        maxWidth={2.5}
                        dotSize={1.5}
                        throttle={16}
                          canvasProps={{
                            width: 500,
                            height: 80,
                            style: {
                              width: '100%',
                              height: '80px',
                              border: 'none',
                              borderRadius: '12px',
                              background: 'white',
                              display: 'block',
                              touchAction: 'none',
                              cursor: 'crosshair',
                              position: 'relative'
                            }
                          }}
                        onEnd={saveEngineerSignature}
                  />
                </div>
                      <div style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '8px'
                      }}>
                        <span style={{
                          fontSize: '12px',
                          color: '#8b1634',
                          fontStyle: 'italic'
                        }}>
                          Draw your signature above
                        </span>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm rounded-pill px-3"
                          style={{
                            background: 'transparent',
                            color: '#8b1634',
                            borderColor: '#8b1634'
                          }}
                          onClick={clearEngineerSignature}>
                          Clear
                        </button>
                </div>
              </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowAddModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{background:'#8b1634', color:'#fff', fontWeight:600}} onClick={handleAdd}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editRecord && (
        <div className="modal fade show" style={adminEditModalOverlayStyle} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={adminEditModalContentStyle}>
              <div className="modal-header" style={adminEditModalHeaderStyle}>
                <h5 className="modal-title" style={adminEditModalTitleStyle}>Edit LSER PPM Daily Checklist</h5>
                <button type="button" className="btn-close btn-close-white" onClick={()=>setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={adminEditModalBodyStyle}>
                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Client Name *</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.clientName || ''} onChange={e=>handleTextChange("clientName", e.target.value, true)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Engineer Name *</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.engineerName || ''} onChange={e=>handleTextChange("engineerName", e.target.value, true)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Site *</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.site || ''} onChange={e=>handleTextChange("site", e.target.value, true)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Date *</label>
                    <div className="site-register-datepicker-wrap">
                      <DatePicker
                        {...siteRegisterDatePickerProps}
                        selected={parseSiteRegisterDate(editRecord?.date)}
                        onChange={(d) => handleTextChange("date", formatSiteRegisterDate(d), true)}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Number of Visits</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.numberOfVisits || ''} onChange={e=>handleTextChange("numberOfVisits", e.target.value, true)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Number of Cameras List</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      style={adminEditInputStyle}
                      value={editRecord?.numberOfCamerasList || ''} 
                      onChange={e=>handleTextChange("numberOfCamerasList", e.target.value.replace(/[^0-9]/g, ''), true)} 
                      pattern="[0-9]*"
                      inputMode="numeric"
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Additional Cameras</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.additionalCameras || ''} onChange={e=>handleTextChange("additionalCameras", e.target.value, true)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Total Number of Cameras</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      style={adminEditInputStyle}
                      value={editRecord?.totalNumberOfCameras || ''} 
                      onChange={e=>handleTextChange("totalNumberOfCameras", e.target.value.replace(/[^0-9]/g, ''), true)} 
                      pattern="[0-9]*"
                      inputMode="numeric"
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>Comments Report</label>
                    <textarea className="form-control" style={adminEditInputStyle} rows="3" value={editRecord?.commentsReport || ''} onChange={e=>handleTextChange("commentsReport", e.target.value, true)} />
                  </div>
                  <div className="col-12 col-md-3">
                    <label style={adminEditLabelStyle}>Print Name</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.printName || ''} onChange={e=>handleTextChange("printName", e.target.value, true)} />
                  </div>
                  <div className="col-12 col-md-3">
                    <label style={adminEditLabelStyle}>Date Print Name</label>
                    <div className="site-register-datepicker-wrap">
                      <DatePicker
                        {...siteRegisterDatePickerProps}
                        selected={parseSiteRegisterDate(editRecord?.datePrintName)}
                        onChange={(d) => handleTextChange("datePrintName", formatSiteRegisterDate(d), true)}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-3">
                    <label style={adminEditLabelStyle}>Date Staff</label>
                    <div className="site-register-datepicker-wrap">
                      <DatePicker
                        {...siteRegisterDatePickerProps}
                        selected={parseSiteRegisterDate(editRecord?.dateStaff)}
                        onChange={(d) => handleTextChange("dateStaff", formatSiteRegisterDate(d), true)}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-3">
                    <label style={adminEditLabelStyle}>Engineer Name 2</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.engineerName2 || ''} onChange={e=>handleTextChange("engineerName2", e.target.value, true)} />
                  </div>
                  <div className="col-12 col-md-3">
                    <label style={adminEditLabelStyle}>Date Engineer Name 2</label>
                    <div className="site-register-datepicker-wrap">
                      <DatePicker
                        {...siteRegisterDatePickerProps}
                        selected={parseSiteRegisterDate(editRecord?.dateEngineerName2)}
                        onChange={(d) => handleTextChange("dateEngineerName2", formatSiteRegisterDate(d), true)}
                      />
                    </div>
                </div>
                  <div className="col-12 col-md-3">
                    <label style={adminEditLabelStyle}>Date Engineer Signature</label>
                    <div className="site-register-datepicker-wrap">
                      <DatePicker
                        {...siteRegisterDatePickerProps}
                        selected={parseSiteRegisterDate(editRecord?.dateEngineerSignature)}
                        onChange={(d) => handleTextChange("dateEngineerSignature", formatSiteRegisterDate(d), true)}
                      />
                    </div>
              </div>

                  {/* MCQ Sections - Edit - Updated to match PowAssessmentPage structure */}
                  <div className="col-12">
                    <label style={{ ...adminEditLabelStyle, color: ADMIN_THEME, fontSize: 14, marginBottom: 10 }}>MCQ Questions List</label>
                    {Array.isArray(editRecord.mcqQuestionsList) && editRecord.mcqQuestionsList.map((section, sidx) => (
                      <div key={sidx} style={{border:'1px solid #eee', borderRadius:8, padding:12, marginTop:8}}>
                        {section.header === 'Steps List' ? (
                          <div>
                            {/* Show main title from API data */}
                            <div className="fw-bold" style={{marginBottom:8, color:'#8b1634', fontSize:'18px'}}>
                              {section.mainTitle || 'Steps List'}
                            </div>
                          </div>
                        ) : (
                        <div className="fw-bold" style={{marginBottom:8}}>{section.header}</div>
                        )}
                        {Array.isArray(section.checkboxOptions) && section.checkboxOptions.length > 0 && (
                          <div className="mb-2" style={{display:'flex', flexWrap:'wrap', gap:16}}>
                            {section.checkboxOptions.map(opt => (
                              <label key={opt} style={{display:'flex', alignItems:'center', gap:6}}>
                                <input type="checkbox" className="form-check-input theme-checkbox" checked={!!section.checkboxValues?.[opt]} onChange={e=>handleMcqCheckboxChange(sidx, opt, e.target.checked, true)} /> {opt}
                              </label>
                            ))}
                  </div>
                        )}
                        {Array.isArray(section.items) && section.items.map((item, iidx) => (
                          <div key={iidx} className="mb-2" style={{paddingLeft:8}}>
                            {/* Main title styling */}
                                {item.isMainTitle ? (
                              <div className="fw-bold" style={{color:'#8b1634', fontSize:'18px', marginBottom:12, marginTop:8}}>
                                    {item.title}
                                  </div>
                                ) : item.isStepHeader ? (
                                  <div className="fw-bold" style={{color:'#8b1634', fontSize:'16px', marginBottom:8, marginTop:12, paddingLeft:16}}>
                                    {item.title}
                                  </div>
                            ) : item.hasCheckbox && !item.textField?.isTextBox ? (
                              /* For checkbox items, show title with checkbox on the right */
                                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6}}>
                                <div className="fw-medium" style={{color:'#8b1634'}}>{item.title || item.text}</div>
                                <input type="checkbox" className="form-check-input theme-checkbox" checked={!!item.checkboxValue} onChange={e=>handleMcqItemCheckboxChange(sidx, iidx, e.target.checked, true)} />
                                      </div>
                            ) : (
                              <div className="fw-medium" style={{marginBottom:6, color:'#8b1634'}}>{item.title || item.text}</div>
                            )}
                            <div style={{display:'flex', flexWrap:'wrap', gap:12, alignItems:'center'}}>
                              {Array.isArray(item.options) && item.options.map(opt => (
                                <label key={opt} style={{display:'flex', alignItems:'center', gap:6}}>
                                  <input type="radio" className="form-check-input theme-checkbox" name={`edit-${sidx}-${iidx}`} checked={item.value === opt} onChange={()=>handleMcqChange(sidx, iidx, opt, true)} /> {opt}
                                </label>
                              ))}
                              {item.textField?.isTextBox && (
                                <div style={{display:'flex', alignItems:'center', gap:12}}>
                  <input
                    type="text"
                                    className="form-control" 
                                    style={{
                                      maxWidth: 320,
                                          fontSize: '16px',
                                      fontFamily: 'system-ui, -apple-system, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
                                      lineHeight: '1.5',
                                          padding: '8px 12px'
                                    }}
                                    placeholder={item.textField?.hintText || ''} 
                                     value={item.textFieldValue !== undefined ? item.textFieldValue : (item.textField?.valueType || item.textField?.value || '')}
                                        onChange={e=>handleMcqTextFieldChange(sidx, iidx, e.target.value, true)} 
                                      />
                                  {/* Only show N/A checkbox if the textField has na property set to true */}
                                  {item.textField?.na && (
                                    <label style={{display:'flex', alignItems:'center', gap:6}}>
                                      <input type="checkbox" className="form-check-input theme-checkbox" checked={!!item.textField?.na} onChange={e=>handleMcqNAChange(sidx, iidx, e.target.checked, true)} /> N/A
                                    </label>
                                    )}
                </div>
                              )}
                                  {/* Show print date field */}
                              {item.printDate?.isPrintDate && (
                                    <div style={{display:'flex', alignItems:'center', gap:12}}>
                                      <label style={{fontSize:'14px', fontWeight:'500'}}>Date:</label>
                                      <div className="site-register-datepicker-wrap" style={{ maxWidth: 200 }}>
                                        <DatePicker
                                          {...siteRegisterDatePickerProps}
                                          selected={parseSiteRegisterDate(item.printDate?.printValue)}
                                          onChange={(d) => {
                                            const updatedSections = [...editRecord.mcqQuestionsList];
                                            updatedSections[sidx].items[iidx].printDate.printValue = formatSiteRegisterDate(d);
                                            setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
                                          }}
                                        />
                                      </div>
                </div>
                              )}
                                  {/* Show signature field - Disabled in Edit */}
                              {item.signature?.isSigned && (
                                    <div style={{display:'flex', flexDirection:'column', gap:8}}>
                                      <label style={{fontSize:'14px', fontWeight:'500'}}>Signature:</label>
                                  <div style={{ ...adminEditSigBoxStyle(), width: 300, minHeight: 80 }}>
                                    {item.signature?.signValue ? (
                                      <img 
                                        src={item.signature.signValue.startsWith('data:image') ? item.signature.signValue : `data:image/png;base64,${item.signature.signValue}`}
                                        alt="Item Signature" 
                                        style={{
                                          maxWidth: '100%',
                                          maxHeight: '60px',
                                          objectFit: 'contain',
                                          border: '1px solid #ddd',
                                          borderRadius: '8px',
                                          padding: '4px',
                                          backgroundColor: 'white',
                                          opacity: 1,
                                          filter: 'contrast(1.1) brightness(1)'
                                        }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    ) : (
                                      <div style={{padding: '20px', color: '#666', fontSize: '14px'}}>
                                        Signature (Disabled in Edit)
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
              </div>

                  {/* Staff Signature - Disabled in Edit */}
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>Staff Signature</label>
                    <div style={adminEditSigBoxStyle()}>
                      {editRecord?.staffSignature ? (
                        <img 
                          src={editRecord.staffSignature.startsWith('data:image') 
                            ? editRecord.staffSignature 
                            : `data:image/png;base64,${editRecord.staffSignature}`}
                          alt="Staff Signature" 
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100px',
                            objectFit: 'contain',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            padding: '4px',
                            backgroundColor: 'white',
                            opacity: 1,
                            filter: 'contrast(1.1) brightness(1)'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div style={{
                          color: '#888',
                          fontSize: '14px',
                          padding: '20px'
                        }}>
                          Signature (Disabled in Edit)
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Engineer Signature - Disabled in Edit */}
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>Engineer Signature</label>
                    <div style={adminEditSigBoxStyle()}>
                      {editRecord?.engineerSignature ? (
                        <img 
                          src={editRecord.engineerSignature.startsWith('data:image') 
                            ? editRecord.engineerSignature 
                            : `data:image/png;base64,${editRecord.engineerSignature}`}
                          alt="Engineer Signature" 
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100px',
                            objectFit: 'contain',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            padding: '4px',
                            backgroundColor: 'white',
                            opacity: 1,
                            filter: 'contrast(1.1) brightness(1)'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div style={{
                          color: '#888',
                          fontSize: '14px',
                          padding: '20px'
                        }}>
                          Signature (Disabled in Edit)
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={adminEditModalFooterStyle}>
                <button type="button" className="btn" style={adminEditCancelBtnStyle} onClick={()=>setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={adminEditSaveBtnStyle} onClick={handleEditSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewRecord && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">View LSER PPM Daily Checklist</h5>
                <button type="button" className="btn-close" onClick={()=>setShowViewModal(false)}></button>
    </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-2 col-4">
                    <label className="fw-bold">User ID:</label>
                    <p>{viewRecord.userId || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label className="fw-bold">Client Name:</label>
                    <p>{viewRecord.clientName || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label className="fw-bold">Engineer Name:</label>
                    <p>{viewRecord.engineerName || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label className="fw-bold">Site:</label>
                    <p>{viewRecord.site || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label className="fw-bold">Date:</label>
                    <p>{viewRecord.date || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label className="fw-bold">Number of Visits:</label>
                    <p>{viewRecord.numberOfVisits || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label className="fw-bold">Number of Cameras List:</label>
                    <p>{viewRecord.numberOfCamerasList || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label className="fw-bold">Additional Cameras:</label>
                    <p>{viewRecord.additionalCameras || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label className="fw-bold">Total Number of Cameras:</label>
                    <p>{viewRecord.totalNumberOfCameras || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-6">
                    <label className="fw-bold">Comments Report:</label>
                    <p>{viewRecord.commentsReport || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-3">
                    <label className="fw-bold">Print Name:</label>
                    <p>{viewRecord.printName || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-3">
                    <label className="fw-bold">Date Print Name:</label>
                    <p>{viewRecord.datePrintName || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-3">
                    <label className="fw-bold">Date Staff:</label>
                    <p>{viewRecord.dateStaff || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-3">
                    <label className="fw-bold">Engineer Name 2:</label>
                    <p>{viewRecord.engineerName2 || 'N/A'}</p>
                </div>
                  <div className="mb-2 col-3">
                    <label className="fw-bold">Date Engineer Name 2:</label>
                    <p>{viewRecord.dateEngineerName2 || 'N/A'}</p>
                </div>
                  <div className="mb-2 col-3">
                    <label className="fw-bold">Date Engineer Signature:</label>
                    <p>{viewRecord.dateEngineerSignature || 'N/A'}</p>
              </div>

                        {/* MCQ Sections - View - Updated to match PowAssessmentPage structure */}
                        <div className="mb-2 col-md-6">
                          <label className="fw-bold" style={{color:'#8b1634'}}>MCQ Questions List</label>
                          {Array.isArray(viewRecord.mcqQuestionsList) && viewRecord.mcqQuestionsList.map((section, sidx) => (
                            <div key={sidx} style={{border:'1px solid #eee', borderRadius:8, padding:12, marginTop:8}}>
                              {section.header === 'Steps List' ? (
                                <div>
                                  {/* Show main title from API data */}
                                  <div className="fw-bold" style={{marginBottom:8, color:'#8b1634', fontSize:'18px'}}>
                                    {section.mainTitle || 'Steps List'}
                                  </div>
                                </div>
                              ) : (
                              <div className="fw-bold" style={{marginBottom:8}}>{section.header}</div>
                              )}
                              {Array.isArray(section.checkboxOptions) && section.checkboxOptions.length > 0 && (
                                <div className="mb-2" style={{display:'flex', flexWrap:'wrap', gap:16}}>
                                  {section.checkboxOptions.map(opt => (
                                    <div key={opt} style={{display:'flex', alignItems:'center', gap:6}}>
                                      <span style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        backgroundColor: section.checkboxValues?.[opt] ? '#d4edda' : '#f8d7da',
                                        color: section.checkboxValues?.[opt] ? '#155724' : '#721c24',
                                        fontWeight: '500'
                                      }}>
                                        {opt}: {section.checkboxValues?.[opt] ? 'âœ“' : 'âœ—'}
                                      </span>
                                    </div>
                                  ))}
                        </div>
                              )}
                              {Array.isArray(section.items) && section.items.map((item, iidx) => (
                                <div key={iidx} className="mb-2" style={{paddingLeft:8, borderBottom:'1px solid #f0f0f0', paddingBottom:'8px'}}>
                                  {item.isMainTitle ? (
                                    <div className="fw-bold" style={{color:'#8b1634', fontSize:'18px', marginBottom:12, marginTop:8}}>
                                      {item.title}
                                    </div>
                                  ) : item.isStepHeader ? (
                                    <div className="fw-bold" style={{color:'#8b1634', fontSize:'16px', marginBottom:8, marginTop:12, paddingLeft:16}}>
                                      {item.title}
                                    </div>
                                  ) : (
                                    <div className="fw-medium" style={{marginBottom:6, color:'#8b1634'}}>{item.title || item.text}</div>
                                  )}
                                  <div style={{display:'flex', flexWrap:'wrap', gap:12, alignItems:'center'}}>
                                    {Array.isArray(item.options) && item.options.length > 0 && (
                                    <div style={{display:'flex', alignItems:'center', gap:6}}>
                                        <strong>Answer:</strong>
                                      <span style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        backgroundColor: item.value === 'Yes' ? '#d4edda' : item.value === 'No' ? '#f8d7da' : '#e2e3e5',
                                        color: item.value === 'Yes' ? '#155724' : item.value === 'No' ? '#721c24' : '#383d41',
                                          fontWeight: '500'
                                      }}>
                                        {item.value || 'Not answered'}
                                      </span>
                                    </div>
                                    )}
                                    {item.hasCheckbox && (
                                      <div style={{display:'flex', alignItems:'center', gap:6}}>
                                        <strong>Checkbox:</strong>
                                        <span style={{
                                          padding: '4px 8px', 
                                          borderRadius: '4px', 
                                          backgroundColor: item.checkboxValue ? '#d4edda' : '#f8d7da',
                                          color: item.checkboxValue ? '#155724' : '#721c24',
                                          fontWeight: '500'
                                        }}>
                                          {item.checkboxValue ? 'âœ“ Checked' : 'âœ— Unchecked'}
                                        </span>
                                      </div>
                                    )}
                                    {item.textField?.isTextBox && (item.textFieldValue || item.textField?.value) && (
                                      <div style={{display:'flex', alignItems:'center', gap:6}}>
                                        <strong>Text Input:</strong>
                                        <span style={{padding: '4px 8px', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6'}}>
                                          {item.textFieldValue || item.textField.value}
                                        </span>
                                      </div>
                                    )}
                                    {item.printDate?.isPrintDate && (
                                      <div style={{display:'flex', alignItems:'center', gap:6}}>
                                        <strong>Date:</strong>
                                        <span style={{
                                          padding: '4px 8px', 
                                          backgroundColor: item.printDate.printValue ? '#d4edda' : '#f8d7da', 
                                          borderRadius: '4px', 
                                          border: '1px solid #dee2e6',
                                          color: item.printDate.printValue ? '#155724' : '#721c24',
                                          fontWeight: '500'
                                        }}>
                                          {item.printDate.printValue || 'Not set'}
                                        </span>
                                      </div>
                                    )}
                                    {item.signature?.isSigned && (
                                      <div style={{display:'flex', alignItems:'center', gap:6}}>
                                        <strong>Signature:</strong>
                                        <div style={{display:'flex', alignItems:'center', gap:4}}>
                                          <span style={{
                                            color: item.signature.signValue ? '#28a745' : '#dc3545', 
                                            fontWeight: '500'
                                          }}>
                                            {item.signature.signValue ? 'âœ“ Signed' : 'âœ— Not signed'}
                                          </span>
                                          {item.signature.signValue && (
                                            <img 
                                              src={item.signature.signValue.startsWith('data:image') ? item.signature.signValue : `data:image/png;base64,${item.signature.signValue}`}
                                              alt="Signature"
                                              style={{width: '80px', height: '40px', border: '1px solid #ccc', borderRadius: '4px'}}
                                            />
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>

                        {/* Staff Signature */}
                        {viewRecord.staffSignature && (
                          <div className="mb-2 col-6">
                            <label className="fw-bold" style={{color:'#8b1634'}}>Staff Signature</label>
                            <div style={{marginTop: '8px'}}>
                              <img 
                                src={viewRecord.staffSignature.startsWith('data:image') ? viewRecord.staffSignature : `data:image/png;base64,${viewRecord.staffSignature}`}
                                alt="Staff Signature"
                                style={{maxWidth: '300px', maxHeight: '150px', border: '1px solid #ccc', borderRadius: '4px'}}
                              />
                            </div>
                          </div>
                        )}

                        {/* Engineer Signature */}
                        {viewRecord.engineerSignature && (
                          <div className="mb-2 col-6">
                            <label className="fw-bold" style={{color:'#8b1634'}}>Engineer Signature</label>
                            <div style={{marginTop: '8px'}}>
                              <img 
                                src={viewRecord.engineerSignature.startsWith('data:image') ? viewRecord.engineerSignature : `data:image/png;base64,${viewRecord.engineerSignature}`}
                                alt="Engineer Signature"
                                style={{maxWidth: '300px', maxHeight: '150px', border: '1px solid #ccc', borderRadius: '4px'}}
                              />
                            </div>
                          </div>
                        )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowViewModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

export default LserPpmDailyChecklistPage;
